import restClient from "api/restClient";
import { RestError } from "api/restClient.types";
import { Button } from "components/Button/Button";
import { Callout } from "components/Form/components/Callout/Callout";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import { getUiCount, getUiCountMax } from "containers/Auth/Auth.selectors";
import { loginCheckEventKey, loginCheckUrl } from "containers/Auth/constants";
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Dispatch } from "redux";
import { isAuthCheckUnauthorized, isUICountWithinLimit } from "utils/authCheckErrors";
import { getWorkspaceLink } from "utils/service";

const UICountThreshold = {
    warning: 0.8,
    blocking: 1.0,
};

interface UICountData {
    ui_count: number;
    ui_count_max: number;
}

export const updateUICountStateCore = (
    dispatch: Dispatch,
    data: UICountData,
    callbacks: {
        setCanConnect: (canConnect: boolean) => void;
        triggerModalCheck: () => void;
    }
) => {
    dispatch({
        type: authActions.STORE_UI_COUNT_DATA,
        response: data,
    });

    callbacks.setCanConnect(isUICountWithinLimit(data.ui_count, data.ui_count_max));
    callbacks.triggerModalCheck();
};

export const useMaxUICountNotification = (workspaceId: string) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const [canConnect, setCanConnect] = useState(false);
    const [forceModalCheck, setForceModalCheck] = useState(0);

    const uiCountMax = useSelector(getUiCountMax);
    const uiCount = useSelector(getUiCount);

    const NavigateToWorkspaceButton = memo(({ onClick }: { onClick?: () => void }) => (
        <Button
            variation="link"
            onClick={() => {
                setCanConnect(true);
                history.push(getWorkspaceLink(workspaceId, "settings"));
                onClick?.();
            }}
            label="workspace settings"
        />
    ));

    const triggerModalCheck = useCallback(() => {
        setForceModalCheck((prev) => prev + 1);
    }, []);

    const updateUICountState = useCallback(
        (data: UICountData) => {
            updateUICountStateCore(dispatch, data, {
                setCanConnect,
                triggerModalCheck,
            });
        },
        [dispatch, setCanConnect, triggerModalCheck]
    );

    const handleAuthCheckFailure = useCallback(
        (status?: number, error?: RestError) => {
            setCanConnect(true);
            if (isAuthCheckUnauthorized(status)) {
                dispatch({
                    type: authActions.SIGN_IN_FAILED,
                });
                return;
            }
            if (error) {
                NotificationService.error(<RestErrorNotification error={error} />);
            }
        },
        [dispatch]
    );

    useEffect(() => {
        const handleAuthCheckResponse = (event) => {
            const { data, success, status } = event.detail;

            if (success === true) {
                updateUICountState(data);
            } else {
                handleAuthCheckFailure(status);
            }
        };

        window.addEventListener(loginCheckEventKey, handleAuthCheckResponse);
        return () => window.removeEventListener(loginCheckEventKey, handleAuthCheckResponse);
    }, [handleAuthCheckFailure, updateUICountState]);

    const refreshUICount = useCallback(async () => {
        try {
            await restClient.get(loginCheckUrl);
        } catch (error) {
            const restError = error as RestError;
            handleAuthCheckFailure(restError?.response?.status, restError);
        }
    }, [handleAuthCheckFailure]);

    useEffect(() => {
        let modal: { destroy: () => void } | undefined;
        if (uiCountMax > 0 && forceModalCheck > 0) {
            const ratio = uiCount / uiCountMax;

            if (ratio >= UICountThreshold.blocking) {
                modal = showConfirmModal({
                    title: "Performance warning",
                    content: (
                        <Callout
                            text={
                                <div>
                                    {uiCount}/{uiCountMax} user interfaces are already connected to
                                    this workspace. Opening additional user interfaces can impact
                                    performance. Please see{" "}
                                    <NavigateToWorkspaceButton onClick={() => modal?.destroy()} />{" "}
                                    to change the limit. Do you really want to connect?
                                </div>
                            }
                            type={"warning"}
                            className="rootCallout"
                        />
                    ),
                    onConfirm: refreshUICount,
                    onCancel: () => {
                        setCanConnect(true);
                    },
                    cancelButtonProps: cancelButtonProps,
                    confirmButtonProps: { className: "button button--primary-blue" },
                    className: "confirmationModal notificationModal",
                    centered: true,
                    confirmText: "Refresh",
                    cancelText: "Connect",
                    zIndex: 10000,
                });
            } else {
                modal?.destroy();

                if (ratio >= UICountThreshold.warning) {
                    NotificationService.warn(
                        <ExpandableNotification
                            title="Performance warning"
                            details={
                                <>
                                    {uiCount}/{uiCountMax} user interfaces are already connected to
                                    this workspace. Opening additional user interfaces can impact
                                    performance. Please see <NavigateToWorkspaceButton /> to change
                                    the limit.
                                </>
                            }
                        />
                    );
                }
            }
        }

        return () => {
            modal?.destroy();
        };
        // exclude NavigateToWorkspaceButton from the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiCount, uiCountMax, forceModalCheck, refreshUICount]);

    return {
        canConnect,
        updateUICountState,
    };
};
